<template>
  <b-row>
    <b-col cols="12" md="10" offset-md="1">
      <b-card class="shadow mx-md-10 px-1 px-md-10 py-2 mb-5">

        <onboarding-stepper
          v-if="currentStep.stepper"
          :currentStep="currentStep"
          :steps="steps"
        />

        <b-row class="pt-5 pb-4  px-sm-5">
          <b-col lg="4" class="px-3">
            <b-jumbotron>
              <p v-if="templates.title"><b>{{ templates.title }}</b></p>
              <p><b>Interests</b></p>
              <p v-if="templates.description">{{ templates.description }}</p>
              <p v-else>A deeper dive into your interests, to build your profile and provide you with tailored suggestions.</p>
            </b-jumbotron>
            <div class="py-3 px-3">
              <b-img
                v-if="company && company.branding && company.branding.onboardingInterestImage && company.branding.onboardingInterestImage.fileLocation"
                :src="company.branding.onboardingInterestImage.fileLocation"
                fluid
              ></b-img>
            </div>
          </b-col>
          <b-col lg="8" class="px-3">
            <b-alert show variant="danger" v-if="!canSubmit" class="mt-3">
              Please fill in all the required fields to continue.
            </b-alert>

            <div
              v-for="({ key, name, description, type, options, departments, required }) in customFields"
              :key="key"
              class="mb-5"
            >
              <b-form-group
                v-if="['STRING', 'ENUM', 'LOCATION', 'TEXTAREA'].includes(type) && (!departments || departments.filter(id => departmentIds.includes(id)).length)"
                :label-for="`input-${key}`"
              >
                <h4><b>{{name}}</b></h4>
                <p class="text-muted">{{description}}</p>
                <b-form-select v-if="type === 'ENUM' && options" :id="`input-${key}`" type="text" :required="required"
                  :options="options" :value="inputValues[key]" @input="value => inputValues[key] = value">
                    <template v-slot:first><option :value="null">-- Please select an option --</option></template>
                </b-form-select>
                <b-form-input
                  v-else-if="type === 'RANGE'"
                  id="`input-${key}`"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                  type="range"
                  min="0"
                  max="200000"
                ></b-form-input>
                <template v-else-if="type === 'LOCATION'">
                  <location-search
                    :value="inputValues[key] ? inputValues[key].location : null"
                    :multiple="true"
                    @input="value => $set(inputValues, key, {
                      remote: inputValues[key] ? inputValues[key].remote : null,
                      location: value,
                    })"
                  />
                  <b-form-checkbox
                      :checked="inputValues[key] ? inputValues[key].remote : false"
                      @input="value => $set(inputValues, key, {
                      remote: value,
                      location: inputValues[key] ? inputValues[key].location : null,
                      })"
                      size="lg"
                  ><span class="pt-2"> Are you interested in opportunities with remote working?</span></b-form-checkbox>
                </template>
                <b-form-textarea
                  v-else-if="type === 'TEXTAREA'"
                  :id="`input-${key}`"
                  type="text"
                  :required="required"
                  :value="inputValues[key]"
                  @input="value => $set(inputValues, key, value)"
                  rows="3"
                  max-rows="6"
                ></b-form-textarea>
                <b-form-input
                  v-else
                  :id="`input-${key}`"
                  type="text"
                  :required="required"
                  :value="inputValues[key]"
                  @input="value => inputValues[key] = value"
                />
              </b-form-group>
              <job-title-selector
                v-if="type === 'JOB_TITLES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <job-business-area-selector
                v-if="type === 'JOB_BUSINESS_AREA'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <multi-selector
                v-if="type === 'MULTI_SELECT'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <job-seniority-selector
                v-if="type === 'JOB_SENIORITY'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                :multiple="true"
                :settings="{ noTitle: true }"
                @input="value => inputValues[key] = value"
              />
              <job-skills-selector
                v-if="type === 'JOB_SKILLS' || type === 'JOB_DEVELOPMENT_SKILLS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :options="options"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <languages-selector
                v-if="type === 'LANGUAGES'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <qualifications-editor
                v-if="type === 'QUALIFICATIONS'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <date-editor
                v-if="type === 'DATE'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
              <job-history-editor
                v-if="type === 'JOB_HISTORY'"
                :value="inputValues[key]"
                :name="name"
                :description="description"
                :required="required"
                @input="value => inputValues[key] = value"
              />
            </div>
          </b-col>
        </b-row>

        <b-row class="px-md-5">
          <b-col class="d-flex justify-content-between align-items-center px-md-4">
            <a class="text-secondary" href="#" @click.prevent="() => $emit('goBack')">Go back</a>
            <b-button @click.prevent="submitForm" class="px-3" variant="primary">Continue</b-button>
          </b-col>
        </b-row>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapRoot } from '@src/mixins/root';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import JobTitleSelector from '@/components/profile/JobTitleSelector';
import JobSkillsSelector from '@/components/profile/JobSkillsSelector';
import MultiSelector from '@/components/profile/MultiSelector';
import LanguagesSelector from '@/components/profile/LanguagesSelector';
import LocationSearch from '@/components/misc/LocationSearch';
import QualificationsEditor from '@/components/profile/QualificationsEditor';
import DateEditor from '@/components/profile/DateEditor';
import JobHistoryEditor from '@/components/profile/JobHistoryEditor';
import JobBusinessAreaSelector from '@/components/profile/JobBusinessAreaSelector';
import JobSenioritySelector from '@/components/profile/JobSenioritySelector';
import RegisterQuestionsQuery from '@/graphql/queries/RegisterQuestions.gql';
import gql from 'graphql-tag';

const templateMap = {
  ONBOARDING_INTEREST_TITLE: 'title',
  ONBOARDING_INTEREST_DESCRIPTION: 'description',
};

const WriteUserQuestionsMutation = gql`
  mutation WriteUserCustomFields($update: [ UpdateUserCustomFieldInput! ]!) {
    updateManyUserCustomFields(update: $update) {
      key
      value
    }
  }
`;

export default {
  name: 'OnboardingSkills',
  components: {
    OnboardingStepper,
    JobTitleSelector,
    JobSkillsSelector,
    LanguagesSelector,
    QualificationsEditor,
    DateEditor,
    JobHistoryEditor,
    LocationSearch,
    JobBusinessAreaSelector,
    JobSenioritySelector,
    MultiSelector
  },
  props: {
    currentStep: {
      type: Object,
      default: () => null,
    },
    steps: {
      type: Array,
      default: () => [],
    }
  },
  data: () => ({
    fatalErr: null,
    err: null,
    canSubmit: true,
    inputValues: {},
  }),
  apollo: {
    company: {
      query: RegisterQuestionsQuery,
      update: ({ company }) => company,
      result({ loading, data }) {
        if (!loading && data && data.company) {
          const { company, user } = data;
          this.$assert(company && company.id, 500, new Error('Failed to load company register questions'));
          this.$assert(user && user.id, 500, new Error('Failed to load user register questions'));
          if (user && Array.isArray(user.customFields) && user.customFields.length) {
            this.inputValues = user.customFields.reduce((values, { key, value }) => {
              try {
                value = JSON.parse(value);
              } catch (e) {
                // not a json
              }
              return Object.assign(values, { [key]: value });
            }, {});
          }
        }
      },
      error(err) {
        this.fatalErr = err;
      }
    },
  },
  computed: {
    ...mapRoot([ 'user' ]),
    templates() {
      const templates = {};
      if (this.company && Array.isArray(this.company.templatedStrings)) {
        const { templatedStrings } = this.company;
        Object.keys(templateMap).forEach(templateKey => {
          const template = templatedStrings.find(el => el.key === templateKey);
          templates[templateMap[templateKey]] = template ? template.value : null;
        });
      }
      console.log(templates);
      return templates;
    },
    departmentIds() {
      const { departments } = this.user;
      return Array.isArray(departments) ? departments.map(({ id }) => id) : [];
    },
    customFields() {
      const { customFields } = this.company || {};
      const fields = (Array.isArray(customFields) ? customFields : []).map(({ options, departments, ...field }) => ({
        ...field,
        options: options ? JSON.parse(options) : null,
        departments: departments ? departments.map(({ id }) => id) : null,
      })).filter(field => field.group === this.currentStep.name);
      console.log(fields);
      return fields;
    },
  },
  methods: {
    async submitForm() {
      try {
        const { inputValues, customFields } = this;

        if (Object.keys(inputValues).length) {
          this.canSubmit = customFields.reduce((out, el) => {
            if (el.required) {
              console.log('required field: ', inputValues[el.key]);
              const val = inputValues[el.key];
              out = out && ((Array.isArray(val) && val.length) || (!Array.isArray(val) && val));
            }
            return out;
          }, true);
          if (!this.canSubmit) return;

          await this.$apollo.mutate({
            mutation: WriteUserQuestionsMutation,
            variables: {
              update: Object.keys(inputValues).map(key => ({
                key,
                value: typeof inputValues[key] === 'string' ? inputValues[key] : (inputValues[key] ? JSON.stringify(inputValues[key]) : null),
              })).filter(({ value }) => value),
            },
            update(store, { data: { updateManyUserCustomFields: fields } }) {
              const data = store.readQuery({ query: RegisterQuestionsQuery });
              data.user.customFields = data.user.customFields.map(field => {
                if (inputValues[field.key]) {
                  field.value = inputValues[field.key];
                }
                return field;
              });
              store.writeQuery({ query: RegisterQuestionsQuery, data });
            },
          });
          Promise.resolve(this.$root.captureLog({
            eventName: 'IM_ONBOARDING_INTEREST_SUCCESS',
            user: { self: true },
          }));
        }

        this.$emit('goNext');
      } catch (err) {
        console.error(err);
        this.err = err;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/onboarding/Interest.vue